import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import SendIcon from '@mui/icons-material/Send';
import { useAlert } from '../context/AlertContext';
import {
  useGetOpenFormulareStatus,
  useGetRatingResponsesForLegalTransaction,
  useSendRating,
} from '../hooks/reactQuerys/RatingFormQueries';
import { RatingResponseDetailDialog } from './RatingResponseDetailDialog';

interface IProps {
  idLegalTransaction: number;
  /** Aktueller Status (100 = Abgeschlossen) — steuert, ob manuell versendet
   *  werden darf. */
  idLegalTransactionState?: number | null;
  closedAt?: string | null;
}

const STATE_ABGESCHLOSSEN = 100;

const STATUS_LABEL: Record<string, string> = {
  pending: 'In Vorbereitung',
  sent: 'Versendet',
  opened: 'Geöffnet',
  submitted: 'Abgegeben',
  expired: 'Abgelaufen',
  failed: 'Fehler',
};

const STATUS_COLOR: Record<
  string,
  'default' | 'primary' | 'success' | 'warning' | 'error' | 'info'
> = {
  pending: 'default',
  sent: 'info',
  opened: 'primary',
  submitted: 'success',
  expired: 'warning',
  failed: 'error',
};

/**
 * Maske zum Durchführen der Bewertung eines Vorgangs: manueller Versand des
 * Bewertungsbogens an die Mandanten (OpenFormulare) und Übersicht der
 * bereits abgegebenen Antworten. Wird im Vorgangs-Tab „Bewertungsbögen"
 * angezeigt.
 */
export const RatingActionPanel: React.FC<IProps> = ({
  idLegalTransaction,
  idLegalTransactionState,
  closedAt,
}) => {
  const { showAlert } = useAlert();
  const { responses, isLoadingResponses, refetchResponses } =
    useGetRatingResponsesForLegalTransaction(idLegalTransaction);
  const { sendRating, isPendingSend } = useSendRating();
  const { ofStatus } = useGetOpenFormulareStatus();

  const isClosed =
    idLegalTransactionState === STATE_ABGESCHLOSSEN ||
    (closedAt != null && closedAt !== '');
  const ofReady = ofStatus?.isReady ?? false;
  const [openDetail, setOpenDetail] = useState<number | null>(null);

  const sortedResponses = useMemo(() => {
    if (!responses) return [];
    return [...responses].sort((a, b) =>
      (b.Created_at || '').localeCompare(a.Created_at || '')
    );
  }, [responses]);

  const handleSend = async () => {
    try {
      const result = (await sendRating(idLegalTransaction)) as {
        sent?: { idLegalClient: number; Email: string }[];
        skipped?: { idLegalClient: number; reason: string }[];
        form?: string;
        error?: string;
      };
      if (result.error) {
        showAlert({ text: result.error, severity: 'error' });
        return;
      }
      const count = result.sent?.length ?? 0;
      const skipped = result.skipped?.length ?? 0;
      showAlert({
        text:
          count === 0
            ? 'Es wurden keine Bewertungsbögen versendet.'
            : `${count} Bewertungsbogen-Mail(s) angestoßen${
                skipped ? `, ${skipped} übersprungen` : ''
              }.`,
        severity: count > 0 ? 'success' : 'warning',
      });
      await refetchResponses();
    } catch (err) {
      console.error(err);
      showAlert({ text: 'Versand fehlgeschlagen.', severity: 'error' });
    }
  };

  return (
    <Box>
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        sx={{ mb: 1 }}
      >
        <Stack direction='row' alignItems='center' spacing={1}>
          <StarIcon color='primary' fontSize='small' />
          <Typography variant='subtitle1'>Versand &amp; Ergebnisse</Typography>
        </Stack>
        {isClosed && ofReady && (
          <Button
            variant='contained'
            startIcon={
              isPendingSend ? <CircularProgress size={16} /> : <SendIcon />
            }
            disabled={isPendingSend}
            onClick={handleSend}
          >
            Bewertungsbogen an Mandanten senden
          </Button>
        )}
      </Stack>

      {!ofReady && (
        <Alert severity='warning' sx={{ mb: 2 }}>
          Der Versand ist nicht möglich: die Erweiterung „OpenFormulare" ist
          nicht aktiviert oder nicht vollständig konfiguriert (Einstellungen →
          Erweiterungen). Automatische Trigger werden erst nach Aktivierung
          ausgeführt.
        </Alert>
      )}

      {ofReady && !isClosed && (
        <Alert severity='info' sx={{ mb: 2 }}>
          Ein manueller Versand ist erst nach Abschluss des Vorgangs möglich.
          Der konfigurierte automatische Versand erfolgt unabhängig davon.
        </Alert>
      )}

      {isLoadingResponses ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : sortedResponses.length === 0 ? (
        <Typography variant='body2' color='text.secondary'>
          Es wurde noch kein Bewertungsbogen an die Mandanten versendet.
        </Typography>
      ) : (
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Mandant</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Versendet</TableCell>
              <TableCell>Abgegeben</TableCell>
              <TableCell align='right'>Bewertung</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedResponses.map(r => {
              const name =
                [r.FirstName, r.LastName].filter(Boolean).join(' ') ||
                r.Email ||
                `#${r.idLegalClient}`;
              const status = (r.Status ||
                'pending') as keyof typeof STATUS_LABEL;
              return (
                <TableRow
                  key={r.idRatingResponse}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setOpenDetail(r.idRatingResponse)}
                >
                  <TableCell>
                    <Stack>
                      <Typography variant='body2'>{name}</Typography>
                      {r.Email && (
                        <Typography variant='caption' color='text.secondary'>
                          {r.Email}
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size='small'
                      label={STATUS_LABEL[status] || status}
                      color={STATUS_COLOR[status] || 'default'}
                      variant={status === 'submitted' ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell>
                    {r.SentAt
                      ? new Date(r.SentAt).toLocaleString('de-DE')
                      : '–'}
                  </TableCell>
                  <TableCell>
                    {r.SubmittedAt
                      ? new Date(r.SubmittedAt).toLocaleString('de-DE')
                      : '–'}
                  </TableCell>
                  <TableCell align='right'>
                    {r.OverallScore != null ? (
                      <Stack
                        direction='row'
                        alignItems='center'
                        justifyContent='flex-end'
                        spacing={0.5}
                      >
                        <Typography variant='body2'>
                          {Number(r.OverallScore).toFixed(2)}
                        </Typography>
                        <StarIcon fontSize='small' color='warning' />
                      </Stack>
                    ) : (
                      '–'
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <RatingResponseDetailDialog
        idRatingResponse={openDetail}
        onClose={() => setOpenDetail(null)}
      />
    </Box>
  );
};
